import type React from 'react';
import { useCallback, useState, useEffect, useRef } from 'react';

import type { ClassNameMap } from '@mui/material/styles';
import useSelector from '@redux/useSelector';
import { userUuidSelector } from '@redux/user/selectors';

import { sendMultiScreenEventToIpad } from 'services/helpers/multiScreen';
import { PlayerEvent } from 'shared-types/player-event';
import type { LogoInterface } from 'types/logo/logo';

interface ImgProps {
  url: string;
  className: string;
}

const LogoImg: React.FC<ImgProps> = ({ className, url }) => {
  return <img className={className} src={url} alt='Logo' />;
};

const makeUrlRandomKey = (length: number) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

const buildLogo = (logoModel: LogoInterface) => {
  const TslLogoImg: React.FC<ClassNameMap> = ({ className }) => {
    const [logo, setLogo] = useState(<div />);
    const userUuid = useSelector(userUuidSelector);
    const assetsUrl = `${process.env.REACT_APP_ASSETS_BASE_URL}/customers`;
    const logoFileName = 'logo';
    const onLoadTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const LOGO_LOAD_EVENT_TIMEOUT = 200;

    const tryToFindCustomLogo = useCallback(
      async (userUuid: string) => {
        const tryToGetLogo = (customLogoUrl: string) =>
          fetch(`${customLogoUrl}${makeUrlRandomKey(10)}`);

        try {
          const fileExtensions = logoModel.getSupportedExtensions();

          for (
            let i = 0, foundImage = null;
            i < fileExtensions.length && foundImage === null;
            i++
          ) {
            const logoModelKey = logoModel.getKey();
            const pathBase = `${assetsUrl}/${userUuid}/${logoFileName}_${logoModelKey}`;
            const filePath = `${pathBase}.${fileExtensions[i]}?${makeUrlRandomKey(30)}`;

            await tryToGetLogo(filePath).then((response) => {
              if (response.ok) {
                foundImage = filePath;
              }
            });
            if (foundImage !== null) {
              return foundImage;
            }
          }
        } catch (error) {
          console.log(error);
        }
        return null;
      },
      [assetsUrl],
    );

    const handleLogoDisplay = useCallback(
      async (userUuid: string | null) => {
        if (userUuid) {
          const image = await tryToFindCustomLogo(userUuid);
          setLogo(<LogoImg className={className} url={image ?? logoModel.getDefault()} />);
          onLoadTimeout.current = setTimeout(() => {
            sendMultiScreenEventToIpad(PlayerEvent.NOTIFY_THAT_LOGO_IS_LOADED);
          }, LOGO_LOAD_EVENT_TIMEOUT);
        } else {
          setLogo(<LogoImg className={className} url={logoModel.getDefault()} />);
        }
      },
      [className, tryToFindCustomLogo],
    );

    useEffect(() => {
      handleLogoDisplay(userUuid);
    }, [handleLogoDisplay, userUuid]);

    useEffect(() => {
      return () => {
        if (onLoadTimeout?.current) {
          clearTimeout(onLoadTimeout.current);
        }
      };
    }, []);

    return logo;
  };

  return TslLogoImg;
};

export default buildLogo;
